import { useEffect, useState } from "react";
import { X, Download, FileText } from "lucide-react";
import toast from "react-hot-toast";
import StatusBadge from "@/components/StatusBadge";
import ShipmentTimeline from "@/features/shipment-management/components/ShipmentTimeline";
import { useShipmentDetail } from "@/features/shipment-management/hooks/useShipmentDetail";
import ShipmentDocumentsPanel from "./ShipmentDocumentsPanel";
import type { DocumentItem } from "@/pages/documents/types";
import { cn } from "@/lib/utils";
import { customerShipmentNumber } from "@/utils/shipmentUtils";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Section({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="px-5 py-4 border-b border-[#E4E7EC]">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[14px] font-[600] text-[#1D2739]">{title}</p>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-[10px] text-[#667185] font-[600] uppercase tracking-[1.5px] mb-0.5">{label}</p>
      <p className="text-[12px] font-[500] text-[#1D2739]">{value || "—"}</p>
    </div>
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shipment: any | null;
  initialMainTab?: "details" | "documents";
}

export default function ShipmentDetailDrawer({
  isOpen,
  onClose,
  shipment,
  initialMainTab = "details",
}: Props) {
  const [timelineTab, setTimelineTab] = useState<"All" | "Vehicle" | "Title">("All");
  const [mainTab, setMainTab] = useState<"details" | "documents">(initialMainTab);

  useEffect(() => {
    if (isOpen) setMainTab(initialMainTab);
  }, [isOpen, initialMainTab, shipment?.id]);

  // Fetch fresh detail from API
  const { data: detail, isLoading } = useShipmentDetail(shipment?.id);

  if (!isOpen || !shipment) return null;

  // Prefer API data, fall back to normalised shipment prop
  const d = detail ?? {};
  const raw = shipment._raw ?? {};

  // Vehicle
  const vehicles = d.vehicles ?? [];
  const firstVehicle = vehicles[0] ?? {};
  const vehicleName = [firstVehicle.year, firstVehicle.make, firstVehicle.model].filter(Boolean).join(" ") || shipment.vehicle;
  const vin = d.vin || firstVehicle.vin || raw.vehicle?.vin || "—";
  const colour = firstVehicle.color || raw.vehicle?.color || "—";

  // Route
  const origin = d.origin ?? raw.pickup ?? {};
  const dest = d.destination ?? raw.dropoff ?? {};
  const originStr = [origin.address, origin.city, origin.state, origin.country].filter(Boolean).join(", ") || shipment.from || "—";
  const originSub = origin.address2 || "";
  const destStr = [dest.address, dest.city, dest.state, dest.country].filter(Boolean).join(", ") || shipment.to || "—";
  const destSub = dest.address2 || (d.desiredDeliveryDate ? `ETA ${new Date(d.desiredDeliveryDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : "");

  // Shipment type
  const isOcean = (d.destinationType ?? raw.shipment_type ?? "").toLowerCase() === "ocean";
  const vesselName = d.vesselName || raw.shipping_details?.vessel || "";
  const bolNumber = d.bolNumber || raw.shipping_details?.bol_number || "";
  const loadingPort = d.loadingPort || raw.shipping_details?.port_of_loading || "";
  const destinationPort = d.destinationPort || raw.shipping_details?.port_of_discharge || "";

  // Payment
  const price = d.price ?? {};
  const postingFee = price.posting_fee ?? raw.pricing?.posting_fee ?? 10;
  const shippingFee = d.offerPrice ?? d.amount ?? raw.pricing?.quoted_price ?? 0;
  const storageFee = price.storage_fee ?? raw.pricing?.storage_fee ?? 40;
  const additionalCharges = price.additional_charges ?? raw.pricing?.additional_charges ?? 20;
  const total = postingFee + shippingFee + storageFee + additionalCharges;
  const totalPaid = d.balance != null ? (total - d.balance) : (raw.pricing?.total_paid ?? 10);
  const outstanding = d.balance ?? (total - totalPaid);

  // Status
  const status = d.status || d.vehicleStatus || shipment.status;
  const shipmentNumber = customerShipmentNumber(shipment);

  const pictures: any[] = raw.pictures ?? [];

  const handleOpenDocument = (document: DocumentItem) => {
    const target = document.fileUrl || document.previewUrl;
    if (target) {
      window.open(target, "_blank", "noopener,noreferrer");
      return;
    }
    toast.error("No file available to open");
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[1000] bg-black/40" onClick={onClose} />

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 z-[1001] flex h-full w-full max-w-[480px] flex-col overflow-hidden rounded-tl-xl rounded-bl-xl border-l border-[#e4e7ec] bg-white"
        style={{
          boxShadow:
            "6px -5px 5px 10px rgba(59,130,246,0.1), -7px 4px 5px 9px rgba(59,130,246,0.1)",
        }}
      >

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="flex-shrink-0 px-4 pt-4 pb-2 pr-2">
          <div className="mb-2 flex items-start justify-between">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="rounded-lg border border-neutral-200 bg-[#f0f2f5] px-2 py-0.5 text-xs font-medium text-neutral-500">
                  #{shipmentNumber}
                </span>
                <StatusBadge status={status} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#1d2739]">{vehicleName}</h2>
                <p className="text-xs font-medium text-[#667185]">{vin}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-[#667185] transition-colors hover:bg-[#F0F2F5]"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Main tabs ───────────────────────────────────────── */}
        <div className="flex flex-shrink-0 gap-2 border-b-[1.5px] border-[#eaecf1] px-2">
          {(["details", "documents"] as const).map((tab) => {
            const isActive = mainTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setMainTab(tab)}
                className="flex flex-col"
              >
                <span
                  className={cn(
                    "px-4 py-2 text-xs font-medium capitalize",
                    isActive ? "text-blue-600" : "text-[#344054]",
                  )}
                >
                  {tab}
                </span>
                <span className={cn("h-px w-full", isActive ? "bg-blue-600" : "bg-[#e4e7ec]")} />
              </button>
            );
          })}
        </div>

        {isLoading && mainTab === "details" ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-2 w-2 animate-bounce rounded-full bg-[#2563EB]"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          </div>
        ) : null}

        {mainTab === "documents" ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <ShipmentDocumentsPanel listingId={shipment.id} onOpenDocument={handleOpenDocument} />
          </div>
        ) : !isLoading ? (
        <div className="flex-1 overflow-y-auto">

          {/* ── Section 2: Vehicle Details ──────────────────────────────── */}
          <Section title="Vehicle details">
            <p className="text-[10px] text-[#667185] font-[600] uppercase tracking-[3px] mb-1">Year/Make/Model</p>
            <p className="text-[14px] font-[600] text-[#1D2739] mb-3">{shipment.vehicle}</p>

            <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-2">
              <Field label="VIN" value={vin} />
              <Field label="Colour" value={colour} />
            </div>

            {/* Route */}
            <div className="bg-[#F9FAFB] rounded-[8px] border border-[#F0F2F5] px-2 py-1">
              <p className="text-[12px] font-[600] text-[#000] mb-3">Route</p>
              <div className="flex items-start gap-3">
                {/* Line */}
                <div className="flex flex-col items-center pt-1 flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-[#2563EB]" />
                  <div className="w-0.5 flex-1 bg-[#DBEAFE] my-1" style={{ minHeight: 32 }} />
                  <div className="w-2 h-2 rounded-full border-2 border-[#2563EB] bg-white" />
                </div>
                {/* Labels */}
                <div className="flex flex-col gap-4 flex-1">
                  <div>
                    <p className="text-[12px] font-[500] text-[#11121A]">{originStr}</p>
                    {originSub && <p className="text-[12px] text-[#667185]">{originSub}</p>}
                  </div>
                  <div>
                    <p className="text-[12px] font-[500] text-[#11121A]">{destStr}</p>
                    {destSub && <p className="text-[12px] text-[#667185]">{destSub}</p>}
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* ── Section 3: Shipping Details (ocean only) ─────────────────── */}
          {isOcean && (vesselName || bolNumber || loadingPort || destinationPort) && (
            <Section title="Shipping details">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Ocean Vessel" value={vesselName} />
                <Field label="BOL Number" value={bolNumber} />
                <Field label="Port of Loading" value={loadingPort} />
                <Field label="Port of Discharge" value={destinationPort} />
              </div>
            </Section>
          )}

          {/* ── Section 4: Payment Details ───────────────────────────────── */}
          <Section
            title="Payment details"
            action={
              <button className="flex items-center gap-1.5 text-[12px] font-[500] text-[#FAFAFA] bg-[#2563EB] px-5 py-1 rounded-[12px] hover:bg-blue-700">
                $ Pay balance
              </button>
            }
          >
            <div className="space-y-2 mb-3 bg-[#F9FAFB] border border-[#F0F2F5] rounded-[8px] px-3 py-2">
              {[
                { label: "Posting Fee", value: `$${postingFee}` },
                { label: "Shipping fees", value: `$${shippingFee.toLocaleString()}` },
                { label: "Storage fees", value: `$${storageFee}` },
                { label: "Additional charges", value: `$${additionalCharges}` },
              ].map((row) => (
                <div key={row.label} className="flex justify-between">
                  <span className="text-[14px] font-[500] text-[#667185]">{row.label}</span>
                  <span className="text-[14px] font-[600] text-[#1D2739]">{row.value}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-[#E4E7EC] pt-2">
                <span className="text-[14px] font-[600] text-[#1D2739]">Total</span>
                <span className="text-[14px] font-[600] text-[#1D2739]">${total.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[14px] text-[#04802E] font-[500]">Total Paid</span>
                <span className="text-[14px] text-[#04802E] font-[500]">-${totalPaid}</span>
              </div>
              <div className="flex justify-between">
                <div>
                  <span className="text-[14px] font-[600] text-[#1D2739]">Outstanding balance</span>
                  
                </div>
                <span className="text-[14px] flex flex-col font-[600] text-[#1D2739]">${outstanding.toLocaleString()}

                  {outstanding > 0 && (
                    <span className="ml-2 text-[10px] text-[#CB1A14] font-[500] -mt-1">Due Today!</span>
                  )}
                </span>
              </div>
            </div>
            <div className="flex gap-4 pt-1">
              <button className="flex items-center gap-1.5 text-[12px] text-[#2563EB] font-[500] hover:text-blue-700">
                <FileText size={14} />
                View Invoice
              </button>
              <button className="flex items-center gap-1.5 text-[12px] text-[#2563EB] font-[500] hover:text-blue-700">
                <Download size={14} />
                Download Receipt
              </button>
            </div>
          </Section>

          {/* ── Section 5: Timeline ──────────────────────────────────────── */}
          <Section
            title="Timeline"
            action={
              <div className="flex gap-3 bg-[#F5F5F5] border border-[#F0F2F5] rounded-[8px] px-1 py-0.5">
                {(["All", "Vehicle", "Title"] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setTimelineTab(tab)}
                    className={`text-[12px] font-[500] transition-colors ${
                      timelineTab === tab ? "text-[#0A0A0A] font-[600] bg-[#fff] rounded-md shadow-sm px-1.5" : "text-[#98A2B3] hover:text-[#667185]"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            }
          >
            <ShipmentTimeline
              listingId={shipment.id}
              filterType={timelineTab === "All" ? undefined : timelineTab.toLowerCase() as "vehicle" | "title"}
            />
          </Section>

          {pictures.length > 0 && (
            <Section title="Pictures">
              <div className="grid grid-cols-4 gap-2">
                {pictures.map((pic: any, i: number) => (
                  <img
                    key={i}
                    src={pic.url}
                    alt={pic.caption ?? `Photo ${i + 1}`}
                    className="aspect-square rounded-lg object-cover border border-[#E4E7EC]"
                  />
                ))}
              </div>
            </Section>
          )}

        </div>
        ) : null}
      </div>
    </>
  );
}
